import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ReactElement, ReactNode, RefObject } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { IncomingInvoiceLine, SupplierContract, SupplierContractTerm, Zahlstatus } from "@kundeninfo/api-client";
import { Modal, ModalFuss, useBestaetigung } from "@kundeninfo/ui";
import { Chip, Eingabe, Hinweis } from "@kundeninfo/ui/me-design";
import type { ObjektStatusArt } from "@kundeninfo/ui/me-design";
import { errorProp } from "../../components/fieldErrors.js";
import { apiMeldung, betragText } from "../../components/vorgangsdetail.js";
import { keepInView } from "../../scroll.js";
import { formatBetrag, formatDatum, formatDezimal } from "../../format.js";
import { berlinerTag } from "../tickets/ticketFormat.js";
import { SUPPLIER_TEXT_WEB as T } from "./texts.js";
import "./Suppliers.css";

/**
 * GEMEINSAME HILFEN der Lieferantenansichten im Web — Lieferverträge (Strang C) UND Eingangsrechnungen (Strang D) an
 * EINER Stelle (Aufgabe 22, Duplikatliste aus Review 13/17): Grenzen des Schemas, die die Masken selbst durchsetzen
 * (Lehre aus Review 16/17: keine Schema-400 aus der Maske), Betrags- und Centform, der heutige Tag, Feldfehler, der
 * Teilzeitraum, die Sicht der Akte, die Liste eines Reiters, Anzeigeformen der Verträge und die kleine gestapelte Frage
 * der direkten Aktionen samt der Zahlungsfrage. Die Kontierungslisten einmal je Ansicht und ihre Namen stehen bei ihren
 * Feldern (`sharedAssignmentLists`, `useAssignmentNames` in `components/KontierungFelder.tsx`), Feldbefunde in
 * `components/fieldErrors.ts`.
 */

/** Die Sicht der Akte (Mandantentrennung E359, `sichtFuer`): `undefined` = das aktive Unternehmen. */
export type SichtWahl = readonly string[] | undefined;

/** Die Liste eines Lieferantenreiters (Verträge, Eingangsrechnungen) — EINE Mechanik für beide (A-K3). */
export type TabList<T> = {
  root: RefObject<HTMLDivElement | null>; items: T[] | null; failed: boolean;
  /** Zählt jedes geglückte Laden — die aufgeklappte Zeile liest damit ihre Teile frisch. */
  stamp: number;
  /** Lädt neu; mit `anchorId` bleibt diese Zeile nach dem Neuzeichnen, wo sie stand (Hausregel, `keepInView`). */
  load: (anchorId?: string | null) => Promise<void>;
};

/**
 * Lädt die Liste eines Reiters, sobald sich `key` (Kunde und Sicht) ändert, und nach jedem Schreibweg über `load`. Eine
 * Antwort, die ein späterer Lauf überholt hat, wird verworfen (Folgezähler, Final-Fixrunde A-W1: nach dem Zuschalten des
 * Unternehmens eines Sprungs darf die ältere, kleinere Liste nicht zuletzt ankommen und die Zielzeile verdrängen). Ein
 * Ladefehler geht an `onError` und steht als `failed` da, nicht als leere Liste.
 */
export function useTabList<T>(attribute: string, key: string, fetch: () => Promise<{ items: T[] }>,
  onError: (e: unknown) => void): TabList<T> {
  const root = useRef<HTMLDivElement | null>(null);
  const [items, setItems] = useState<T[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [stamp, setStamp] = useState(0);
  const run = useRef(0);
  const settle = useRef<(() => void) | null>(null);
  useLayoutEffect(() => { settle.current?.(); settle.current = null; });
  const latest = useRef({ fetch, onError });
  latest.current = { fetch, onError };
  const load = useCallback(async (anchorId: string | null = null): Promise<void> => {
    const n = ++run.current;
    const row = anchorId === null ? null : root.current?.querySelector(`[${attribute}="${anchorId}"]`) ?? null;
    const keep = row === null ? null : keepInView(row);
    try {
      const r = await latest.current.fetch();
      if (n !== run.current) return;
      settle.current = keep;
      setItems(r.items);
      setFailed(false);
      setStamp((x) => x + 1);
    } catch (e) {
      if (n !== run.current) return;
      setFailed(true);
      latest.current.onError(e);
    }
  }, [attribute]);
  useEffect(() => { void load(); }, [load, key]);
  return { root, items, failed, stamp, load };
}

/**
 * DIE LEISTE EINES LIEFERANTENBEREICHS (Verträge, Eingangsrechnungen) — EINE Mechanik für beide (A-K3): lädt bei jeder
 * neuen Abfrage (ihr JSON ist der Schlüssel; eine überholte Antwort fällt weg) und hält die Leiste nach einem
 * Filterwechsel an ihrer Stelle (`change` merkt sie vor, nach dem Neuzeichnen steht sie wieder dort, `keepInView`). Ein
 * Ladefehler ist kein leerer Bereich.
 */
export function useAreaQuery<Q, R>(query: Q, fetch: (q: Q) => Promise<R>, failedText: string): {
  data: R | null; error: string | null; toolbar: RefObject<HTMLDivElement | null>; change: (apply: () => void) => void;
  /** Lädt dieselbe Abfrage neu, ohne zu leeren (nach einem Schreibweg); `anchor` bleibt an seiner Stelle. */
  reload: (anchor: Element | null) => void;
  /** Zählt jedes geglückte Laden — aufgeklappte Objekte lesen damit ihre Teile frisch. */
  stamp: number;
} {
  const [data, setData] = useState<R | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [round, setRound] = useState(0);
  const [stamp, setStamp] = useState(0);
  const toolbar = useRef<HTMLDivElement | null>(null);
  const anchor = useRef<(() => void) | null>(null);
  const settle = useRef<(() => void) | null>(null);
  useLayoutEffect(() => { settle.current?.(); settle.current = null; });
  const key = JSON.stringify(query);
  useEffect(() => {
    let alive = true;
    fetch(query).then((r) => {
      if (!alive) return;
      settle.current = anchor.current;
      anchor.current = null;
      setData(r);
      setError(null);
      setStamp((n) => n + 1);
    }, (e: unknown) => { if (alive) setError(apiMeldung(e, failedText)); });
    return () => { alive = false; };
    // Die Abfrage IST der Schlüssel; `round` zählt die Neuladungen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, round]);
  const change = (apply: () => void): void => {
    if (toolbar.current !== null) anchor.current = keepInView(toolbar.current);
    apply();
  };
  const reload = (row: Element | null): void => {
    anchor.current = row === null ? null : keepInView(row);
    setRound((n) => n + 1);
  };
  return { data, error, toolbar, change, reload, stamp };
}

/** Ein Status-Chip der Leiste an- oder abschalten; die Wahl steht in der Ordnung der Chips, alle an heißt „Alle“. */
export function toggleState<S extends string>(all: readonly S[], chosen: readonly S[], s: S): S[] {
  const next = all.filter((x) => (x === s ? !chosen.includes(s) : chosen.includes(x)));
  return next.length === all.length ? [] : next;
}

/** Spalten und Schema (Migration 060, Aufgabe 12): Titel 200, Referenz 100, Notiz der Kondition 500, Vertragsnotiz wie
 *  die Stammdaten 20 000; Laufzeitwerte höchstens 120, Versatz ±366 Tage; ein Betrag „-1.234.567.890,12“ hat 17 Zeichen
 *  (EIN Wert für Verträge und Eingangsrechnungen, A-K3 — ein Vorzeichen prüft `amountForApi`). */
export const TITLE_MAX = 200;
export const REFERENCE_MAX = 100;
export const TERM_NOTE_MAX = 500;
export const NOTE_MAX = 20000;
export const AMOUNT_FIELD_MAX = 17;
export const TERM_VALUE_MAX = 120;
export const DUE_OFFSET_MAX_DAYS = 366;
export const DUE_DAY_MAX = 31;

/** Zahlweisen und Steuerfälle — Vertrag, Kondition und Eingangsrechnung wählen aus derselben Liste (Texte in
 *  `SUPPLIER_TEXT_WEB.paymentMethod`/`taxCase`). */
export const PAYMENT_METHODS: readonly SupplierContract["paymentMethod"][] = ["TRANSFER", "DIRECT_DEBIT", "STANDING_ORDER", "CARD"];
export const TAX_CASES: readonly IncomingInvoiceLine["taxCase"][] = ["DOMESTIC", "REVERSE_CHARGE", "INTRA_EU", "EXEMPT"];

/** Planbeginn bei Altverträgen (Dirk 511), Spiegel von `PLAN_FROM_DEFAULT`/`effectivePlanFrom` der Domäne (web importiert
 *  sie nicht): ohne Erfassungsmonat ab diesem Tag, frühestens ab dem Vertragsbeginn. Das Jahr der Maske hat vier Stellen. */
export const PLAN_FROM_DEFAULT = "2026-01-01";
export const PLAN_YEAR_MIN = 1000;
export const PLAN_YEAR_MAX = 9999;
export const defaultPlanFrom = (validFrom: string): string => (validFrom > PLAN_FROM_DEFAULT ? validFrom : PLAN_FROM_DEFAULT);

// ------------------------------------------------------------------------------------------- Beträge

/** Betrag der Maske: Vorkommastellen mit oder ohne Tausenderpunkte, höchstens zwei nach dem Komma. */
const AMOUNT_INPUT = /^(-?)(\d+|\d{1,3}(?:\.\d{3})+)(?:,(\d{1,2}))?$/;
/** Eindeutig mit Dezimalpunkt („41.93“, ein oder zwei Stellen danach) — „1.500“ bleibt Tausenderpunkt. */
const AMOUNT_DOT = /^(-?)(\d+)\.(\d{1,2})$/;
/** Höchstens zehn Vorkommastellen (`AMOUNT_RE` der Domäne, DECIMAL(12,2)). */
const AMOUNT_DIGITS = 10;

/**
 * Ein Betrag der Maske („1.234,5“) in der Betragsform der API („1234.50“); unlesbar oder zu lang → `null`. Ein Vorzeichen
 * gilt nur mit `signed` (Beträge einer Gutschrift); Konditionen und Zahlungen sind nicht negativ.
 */
export function amountForApi(text: string, o: { signed?: boolean } = {}): string | null {
  const m = AMOUNT_INPUT.exec(text.trim()) ?? AMOUNT_DOT.exec(text.trim());
  if (m === null || (m[1] === "-" && o.signed !== true)) return null;
  const whole = m[2]!.replace(/\./g, "");
  return whole.length > AMOUNT_DIGITS ? null : `${m[1]!}${whole}.${(m[3] ?? "").padEnd(2, "0")}`;
}
/** Betragsform der API als Feldtext („1200,00“). */
export const amountField = (api: string): string => betragText(Number(api));
/** Betragsform der API in Cent — immer zwei Nachkommastellen, also ohne Rundung und ohne Gleitkomma. */
export const cents = (api: string): bigint => BigInt(api.replace(".", ""));
/** Cent als Feldtext („7,97“) und als Anzeige („7,97 €“). */
export const centsField = (c: bigint): string => betragText(Number(c) / 100);
export const centsMoney = (c: bigint, currency: string): string => formatBetrag(Number(c) / 100, currency);
/** Cent in der Betragsform der API; sprengt der Betrag die zehn Vorkommastellen → `null`. */
export function centsForApi(c: bigint): string | null {
  const digits = (c < 0n ? -c : c).toString().padStart(3, "0");
  const whole = digits.slice(0, -2);
  return whole.length > AMOUNT_DIGITS ? null : `${c < 0n ? "-" : ""}${whole}.${digits.slice(-2)}`;
}

/** Eine ganze Zahl der Maske zwischen `min` und `max`: leer → `null`, sonst die Zahl oder `undefined` (ungültig). */
export function wholeNumber(text: string, min: number, max: number): number | null | undefined {
  const t = text.trim();
  if (t === "") return null;
  if (!/^-?\d+$/.test(t)) return undefined;
  const n = Number(t);
  return n >= min && n <= max ? n : undefined;
}

/** Der heutige Berliner Kalendertag. */
export const today = (): string => berlinerTag(new Date().toISOString());

/** Die Feldbefunde einer 422 als Karte Feld → Satz (leer ohne Befunde). */
export function fieldErrorsOf(e: unknown): Record<string, string> {
  if (!(e instanceof ApiError) || e.status !== 422) return {};
  return Object.fromEntries((e.fields ?? []).map((f) => [f.field, f.message]));
}

/** Der Satz eines Fehlschlags: eine Fachregel nennt ihren Satz (etwa „Ein entfallener Posten wird nicht bezahlt.“),
 *  nicht die Sammelmeldung; sonst die Meldung der API. */
export function failureText(e: unknown): string {
  const reported = Object.values(fieldErrorsOf(e));
  return reported.length > 0 ? reported.join(" ") : apiMeldung(e, T.actionFailed);
}

const MONTH = new Intl.DateTimeFormat("de-DE", { month: "short", year: "numeric" });

/** Ein Teilzeitraum (Posten, Leistungszeitraum): ein Kalendermonat als „Jan. 2026“, sonst „01.01.2026–31.03.2026“. */
export function periodText(start: string, end: string): string {
  if (start.slice(0, 7) !== end.slice(0, 7)) return `${formatDatum(start)}–${formatDatum(end)}`;
  const [year, month] = start.split("-").map(Number);
  return MONTH.format(new Date(year!, month! - 1, 1));
}

// ------------------------------------------------------------------------------------------- Anzeige der Verträge

export type Cycle = SupplierContractTerm["cycle"];

/** „49,90 € / Monat“ — der Betrag einer Kondition gilt je Posten (Spec „Laufzeit und Konditionen“). */
export const perCycleText = (net: string, cycle: Cycle): string => `${formatDezimal(net)} ${T.perCycle[cycle]}`;

/** „01.01.2026 – 31.12.2026“ bzw. „ab 01.01.2026“. */
export const runtimeText = (c: Pick<SupplierContract, "validFrom" | "validTo">): string =>
  T.runtime(formatDatum(c.validFrom), c.validTo === null ? null : formatDatum(c.validTo));

const STATE_LEVEL = { PLANNED: "aus", RUNNING: "ok", TERMINATED: "warnung", ENDED: "aus" } as const;

/** Der Zustand eines Vertrags als Text und Stufe (PF11) — EINE Abbildung für das Kennzeichen und den Status am `Objekt`:
 *  „gekündigt zum 31.12.2026“ trägt das Vertragsende. */
export function contractStatus(c: Pick<SupplierContract, "state" | "validTo">): { text: string; art: ObjektStatusArt } {
  const s = c.state;
  return { text: s === "TERMINATED" ? T.state.TERMINATED(formatDatum(c.validTo ?? "")) : T.state[s], art: STATE_LEVEL[s] };
}

/** Das Status-Kennzeichen (rahmenlos, Fläche nach Stufe). */
export function ContractStateChip({ contract }: { contract: Pick<SupplierContract, "state" | "validTo"> }): ReactElement {
  const s = contractStatus(contract);
  return <Chip statusArt={s.art}>{s.text}</Chip>;
}

// ------------------------------------------------------------------------------------------- Direkte Aktionen

type Failure = { text: string | null; stale: boolean; fields: Record<string, string> };

/**
 * DIE KLEINE GESTAPELTE FRAGE einer direkten Aktion (Zahlung bestätigen, Kündigung erfassen, Storno; Dirk 216c: gefragt
 * wird vor direkten Aktionen, nicht vor dem Speichern eines Panels). Sie bleibt während der Aktion offen und zeigt einen
 * Fehlschlag in sich (Lehre aus Review 17, W2):
 *
 *  - eine 422 an den eigenen Feldern der Frage (`fields`) steht an diesen Feldern — derselbe
 *    Versuch mit anderen Werten bleibt möglich;
 *  - jede andere 422 (Fachregel am Datensatz, etwa „entfallener Posten“) und die 409 (veralteter Stand) sperren den
 *    Knopf — derselbe Versuch scheiterte wieder; die Frage sagt, dass das Schließen den aktuellen Stand holt.
 *
 * Nach einem Fehlschlag lädt das Schließen neu (`onStale`), sonst nur `onClose`. `danger` gibt der Frage die
 * Gefahr-Gestalt (Entscheid 32: Titel in Fehlerfarbe, Aktionsknopf rot, Einstiegsfokus auf „Abbrechen“).
 */
export function ActionQuestion<R>(p: {
  title: string; confirmText: string; ready: boolean; fields: readonly string[]; danger?: boolean;
  run: () => Promise<R>; onDone: (r: R) => void; onClose: () => void;
  onStale: () => void; children: (errors: Readonly<Record<string, string>>) => ReactNode;
}): ReactElement {
  const [running, setRunning] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);
  async function go(): Promise<void> {
    setRunning(true);
    setFailure(null);
    try {
      p.onDone(await p.run());
    } catch (e) {
      const reported = Object.entries(fieldErrorsOf(e));
      const own = reported.length > 0 && reported.every(([f]) => p.fields.includes(f));
      setFailure(own ? { text: null, stale: false, fields: Object.fromEntries(reported) }
        : { text: failureText(e), stale: e instanceof ApiError && [409, 422].includes(e.status), fields: {} });
      setRunning(false);
    }
  }
  const close = (): void => {
    if (running) return;
    if (failure === null) p.onClose(); else p.onStale();
  };
  const danger = p.danger === true ? { gefahr: true } : {};
  return (
    <Modal titel={p.title} frage {...danger} onAbbrechen={close} abbrechenGesperrt={running}>
      {failure !== null && failure.text !== null && (
        <Hinweis art="fehler">{failure.stale ? `${failure.text} ${T.reloadOnClose}` : failure.text}</Hinweis>
      )}
      {/* Sätze und Felder als direkte Kinder: den Rumpf einer Frage (Raster, Abstand) setzt me-dialog selbst (W-6). */}
      {p.children(failure?.fields ?? {})}
      <ModalFuss speichernText={p.confirmText} {...danger} speichernGesperrt={!p.ready || running || failure?.stale === true}
        abbrechenGesperrt={running} onSpeichern={() => { void go(); }} onAbbrechen={close} />
    </Modal>
  );
}

/**
 * DIE ZAHLUNGSFRAGE — am Posten eines Vertrags (Strang C) und an der Eingangsrechnung (Strang D) dieselbe (Ruling
 * Re-Review 13). Eingegeben wird die EINE Zahlung; der Server führt `paidAmount` als Summe aller Zahlungen, gesendet
 * wird deshalb das bisher Gezahlte plus diese Zahlung in Cent — eine Teilzahlung überschreibt nie die vorige. Nach einer
 * früheren Zahlung nennt die Frage „bisher bezahlt ‹x› · danach ‹Summe›“.
 *
 * Vorbelegung: der offene Rest; ohne offenen Rest und ohne gespeicherte Zahlung („angenommen bezahlt“) der Brutto- bzw.
 * Zahlbetrag — ein Klick bestätigt ihn; ist schon alles bezahlt, bleibt das Feld leer und „Bestätigen“ gesperrt (sonst
 * schickte ein Klick ohne Eingabe bisher + Brutto, also das Doppelte). Ein leeres Feld hat einen eigenen Satz ohne
 * Betrag.
 *
 * Betrag 0 ist IMMER der Widerspruch (Spec „Zahlstatus“: Lastschrift zurückgegangen; ein Hinweis am Betrag sagt es):
 * gesendet wird „0.00“, nicht die bisherige Summe; der Satz nennt, was zurückging (das bisher Bezahlte, ohne Zahlung der
 * Brutto- bzw. Zahlbetrag), und „danach 0,00 €“ steht darunter. Ein negativer Betrag (offenes Guthaben einer
 * Eingangsrechnung, nur mit `signed`) ist eine Erstattung: „Erstattung über 35,50 € erhalten am …?“. Eine Überzahlung
 * ist zulässig (Spec „≥ Zahlbetrag“); sprengt die Summe die Betragsform, steht ein eigener Satz am Betrag und der Knopf
 * bleibt gesperrt.
 */
export function PaymentQuestion<R>(p: {
  currency: string; paidAmount: string | null; payment: Zahlstatus; gross: string;
  /** Mit Vorzeichen (Eingangsrechnung: ein offenes Guthaben wird mit negativem Betrag als erhalten bestätigt). */
  signed?: boolean;
  run: (paidOn: string, paidAmount: string) => Promise<R>; onDone: (r: R) => void; onClose: () => void; onStale: () => void;
}): ReactElement {
  const [paidOn, setPaidOn] = useState(today);
  const [amount, setAmount] = useState(() => (p.payment.openAmount !== 0 ? betragText(p.payment.openAmount)
    : p.paidAmount === null ? betragText(Number(p.gross)) : ""));
  const money = (c: bigint): string => centsMoney(c, p.currency);
  const signed = p.signed === true;
  const api = amountForApi(amount, { signed });
  const entered = api === null ? null : cents(api);
  const before = p.paidAmount === null ? 0n : cents(p.paidAmount);
  const contradiction = entered === 0n;
  const total = entered === null ? null : contradiction ? "0.00" : centsForApi(before + entered);
  const day = paidOn === "" ? T.pending : formatDatum(paidOn);
  const title = amount.trim() === "" ? T.confirmPaymentWithoutAmount(day)
    : entered === null ? T.confirmPayment(amount, day)
      : contradiction ? T.confirmContradiction(money(before !== 0n ? before : cents(p.gross)), day)
        : entered < 0n ? T.confirmRefund(money(-entered), day) : T.confirmPayment(money(entered), day);
  // Ein leeres Feld ist noch keine falsche Eingabe — es sperrt nur den Knopf.
  const amountError = amount.trim() === "" ? undefined
    : api === null ? (signed ? T.amountInvalidSigned : T.amountInvalid) : total === null ? T.paymentSumTooLarge : undefined;
  return (
    <ActionQuestion title={title} confirmText={T.confirm} ready={total !== null && paidOn !== ""}
      fields={["paidOn", "paidAmount"]} onDone={p.onDone} onClose={p.onClose} onStale={p.onStale}
      run={() => p.run(paidOn, total!)}>
      {(errors) => (
        <>
          {before !== 0n && total !== null && (
            <p className="sup__muted" role="status">{T.paidSoFar(money(before), money(cents(total)))}</p>
          )}
          <Eingabe art="datum" beschriftung={T.paidOn} wert={paidOn} pflicht {...errorProp(errors.paidOn)}
            onGeaendert={(e) => setPaidOn(e.wert)} />
          <Eingabe art="dezimal" einheit={T.euro} beschriftung={T.paidAmount} wert={amount} pflicht maxLength={AMOUNT_FIELD_MAX}
            hilfe={T.contradictionHint} {...errorProp(errors.paidAmount ?? amountError)} onGeaendert={(e) => setAmount(e.wert)} />
        </>
      )}
    </ActionQuestion>
  );
}

/**
 * „ZAHLUNG ZURÜCKNEHMEN“ (Aufgabe 33; Folgepaket Eingangsrechnungen und Verträge) — am Posten und an der Eingangsrechnung
 * dieselbe gestapelte Frage (`bestaetigen()`, Hausregel: direkte datenändernde Aktion) mit Betrag und Datum der
 * gespeicherten Zahlung, bei Betrag 0 als Widerspruch. Gesendet werden `paidOn` und `paidAmount` beide `null` (der
 * Server nimmt die Zahlung zurück); danach folgt der Zahlstatus wieder Fälligkeit und Zahlweise. `null` heißt: nicht
 * gefragt oder abgelehnt, nichts gesendet; sonst der Satz eines Fehlschlags oder `null` — in beiden Fällen lädt der
 * Aufrufer neu (nie steht ein veralteter Stand da).
 */
export function useWithdrawPayment(): (w: { paidAmount: string; paidOn: string | null; currency: string;
  run: () => Promise<unknown> }) => Promise<{ failure: string | null } | null> {
  const { bestaetigen } = useBestaetigung();
  return async (w) => {
    const datum = w.paidOn === null ? T.none : formatDatum(w.paidOn);
    const amount = cents(w.paidAmount);
    const titel = amount === 0n ? T.confirmWithdrawContradiction(datum) : T.confirmWithdraw(centsMoney(amount, w.currency), datum);
    if (!(await bestaetigen({ titel, text: T.withdrawText, bestaetigenText: T.withdrawConfirm }))) return null;
    try {
      await w.run();
      return { failure: null };
    } catch (e) {
      return { failure: failureText(e) };
    }
  };
}
